import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { GeneratedCover } from '../components/ui/GeneratedCover';
import { Button } from '../components/ui/Button';
import { Clock, BookOpen, Lock, PlayCircle, Plus, Share2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getBook, listBooks } from '../services/api';

export const BookDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [book, setBook] = useState<any>(null);
  const [recs, setRecs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const data = await getBook(id);
        setBook(data);
        // lightweight recommendations: fetch list and filter out current
        const all = await listBooks();
        setRecs((all.books || []).filter((b: any) => b.id !== id).slice(0, 3));
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleShare = async () => {
    if (!book) return;
    const shareData = {
      title: book.title,
      text: `Read ${book.title} by ${book.author} on Readsy.`,
      url: window.location.href,
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else if (navigator.share) {
        await navigator.share(shareData);
      } else {
        throw new Error('Web Share API not supported');
      }
    } catch (error) {
      console.warn('Share failed:', error);
      alert(`Share this book:\n${book.title}\n\nLink: ${window.location.href}`);
    }
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;
  if (!book) return <div className="p-8 text-center">Book not found</div>;

  return (
    <div className="pb-24 animate-in fade-in duration-500">
      {/* Book Hero Section */}
      <div className="relative pt-8 px-6 pb-8 flex flex-col items-center border-b border-black/5 dark:border-white/5">
        <div className="mb-6 shadow-2xl rounded-sm transition-transform hover:scale-105 duration-300">
           <GeneratedCover title={book.title} author={book.author} genre={book.genre} size="xl" />
        </div>
        
        <h1 className="font-serif text-2xl md:text-3xl font-bold text-center mb-2 leading-tight max-w-md">{book.title}</h1>
        <p className="text-base font-medium opacity-60 mb-5">{book.author}</p>

        {/* Metadata */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs opacity-60 mb-8 uppercase tracking-wider font-bold">
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-brand-blue"></span> {book.genre}</span>
          <span className="flex items-center gap-1.5"><BookOpen size={14} /> {book.pages || 0} pages</span>
          <span className="flex items-center gap-1.5"><Clock size={14} /> ~4h read</span>
        </div>

        {/* CTAs */}
        <div className="flex gap-3 w-full max-w-sm mb-6">
          {book.chapters?.length ? (
            <Link to={`/book/${book.id}/read/${book.chapters[0].id}`} className="flex-1">
              <Button fullWidth className="gap-2 h-12 text-base shadow-brand-blue/20 shadow-xl">
                <PlayCircle size={20} fill="currentColor" className="text-white/20" />
                Read Chapter 1
              </Button>
            </Link>
          ) : (
            <Button fullWidth disabled>
              No chapters yet
            </Button>
          )}
          {user && (
            <Button variant="secondary" className="px-4 h-12" aria-label="Add to Library">
              <Plus size={20} />
            </Button>
          )}
        </div>

        {/* Share Button */}
        <button 
          onClick={handleShare}
          className="flex items-center gap-2 text-sm font-medium opacity-50 hover:opacity-100 hover:text-brand-blue transition-all px-4 py-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
        >
           <Share2 size={16} />
           <span>Share this book</span>
        </button>
      </div>

      {/* About the Book */}
      <section className="px-6 py-8">
        <h2 className="font-sans font-bold text-lg mb-3">About the Book</h2>
        <p className="text-base leading-relaxed opacity-80 font-serif text-justify">
          {book.description || 'No description yet.'}
        </p>
      </section>

      {/* Table of Contents */}
      <section className="px-6 py-8">
        <h2 className="font-sans font-bold text-lg mb-4">Chapters</h2>
        <div className="space-y-1">
          {book.chapters?.map((chapter: any, index: number) => (
            <Link 
              key={chapter.id} 
              to={`/book/${book.id}/read/${chapter.id}`}
              className={`flex items-center justify-between p-4 rounded-xl transition-colors border border-transparent ${
                chapter.is_free || user 
                  ? 'hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer opacity-100' 
                  : 'opacity-60 hover:bg-black/5 dark:hover.bg-white/5'
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="text-xs font-mono opacity-30 w-4 text-right">{index + 1}</span>
                <div>
                    <span className="text-sm font-medium block">{chapter.title}</span>
                    <span className="text-[10px] opacity-40 font-medium uppercase tracking-wider">{chapter.word_count} words</span>
                </div>
              </div>
              {chapter.is_free ? (
                <span className="text-[10px] bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 px-2 py-1 rounded-md font-bold">Free</span>
              ) : (
                !user && <Lock size={16} className="opacity-30" />
              )}
            </Link>
          ))}
          {!book.chapters?.length && <div className="text-sm opacity-60">No chapters yet.</div>}
        </div>
      </section>

      {/* Recommended Books */}
      <section className="px-6 py-8 border-t border-black/5 dark:border-white/5">
        <h2 className="font-sans font-bold text-lg mb-6">You May Also Like</h2>
        {recs.length === 0 ? (
          <div className="text-sm opacity-60">No recommendations yet.</div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar snap-x">
            {recs.map(rec => (
              <Link key={rec.id} to={`/book/${rec.id}`} className="snap-start shrink-0 w-28 group">
                <GeneratedCover title={rec.title} author={rec.author} size="sm" className="mb-2 shadow-sm" />
                <h3 className="font-serif font-bold text-xs leading-tight group-hover:text-brand-blue line-clamp-2">{rec.title}</h3>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
